import { deleteCompanyApi, useCompany } from "@/api/services/admin/company";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { Button } from "@/components/elements/Button";
import { LoadingPage } from "@/components/elements/Loading/Loading";
import { Tag } from "@/components/elements/Tag";
import { TopPage } from "@/components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { CompanyPaymentTable } from "@/features/company/components/CompanyPaymentTable";
import { CompanyStatusTag } from "@/features/company/components/CompanyStatusTag";
import { CompanySummary } from "@/features/company/components/CompanySummary";
import { CompanyUserTable } from "@/features/company/components/CompanyUserTable";
import { useDialog } from "@/hooks/useDialog";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { black } from "@/theme/colors";
import { ECompanyContractStatus } from "@/types/enum";
import { showError } from "@/utils/error";
import { getDynamicRoute } from "@/utils/url";
import { Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC } from "react";
import { Link, useNavigate } from "react-router-dom";

export const CompanyDetailPage: FC = () => {
  const confirmDelete = useDialog();
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const { data: companyResult, isLoading } = useCompany();
  const companyData = companyResult?.data;

  const handleDelete = async () => {
    try {
      if (!companyResult?.data.id) return;
      const agreeToDelete = await confirmDelete.show();
      if (!agreeToDelete) return;
      const rs = await deleteCompanyApi(String(companyResult.data.id));
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      navigate(AppRoutes.duringContractCompanyList);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY.FETCH_LIST] });
    } catch (error) {
      showError(error);
    }
  };

  if (isLoading || !companyData) return <LoadingPage />;

  return (
    <Stack gap={3}>
      <TopPage
        title={
          <Typography variant="head32Bold">
            {companyData.companyName}
            <Typography component="span" variant="sub16Semi">
              ID: {companyData.companyCode ?? "----"}
            </Typography>
          </Typography>
        }
        backUrl={AppRoutes.duringContractCompanyList}
        rightContent={
          <Stack direction="row" alignItems="center" gap={3}>
            <CompanyStatusTag status={companyData.contractStatus} />
            {companyData.deletedAt && <Tag enablePoint variant="bold" title="削除済み" color={black[95]} pointColor={black[70]} />}
            {!companyData.deletedAt && (
              <Button component={Link} variant="primary" to={getDynamicRoute(AppRoutes.companyEdit, { id: companyData.id })}>
                編集
              </Button>
            )}
            {(companyData.contractStatus == ECompanyContractStatus.EXPIRED_CONTRACT ||
              companyData.contractStatus == ECompanyContractStatus.TRIAL_CANCELLED) &&
              !companyData.deletedAt && (
                <Button variant="outline" onClick={handleDelete}>
                  削除
                </Button>
              )}
          </Stack>
        }
      />
      <CompanySummary />
      <CompanyUserTable />
      <CompanyPaymentTable />
      <ConfirmDeleteModal
        open={confirmDelete.open}
        onClose={confirmDelete.cancel}
        onOk={confirmDelete.confirm}
        message="企業を削除してもよろしいですか？"
      />
    </Stack>
  );
};
